import { computed, observable, runInAction } from "mobx"
import { useEffect } from "react"

import { HasIdDisplay, HasIdNameDisplay } from "~/@shared/api-types/id-name-display"
import { fetchGetLongInternshipPurpose, GetLongInternshipPurpose } from "~/api/long_internship_purpose/get"
import { fetchGetMasterSchools, GetMasterSchools } from "~/api/master/schools/get"
import { fetchGetSkills } from "~/api/skills/get"
import { fetchGetStudentApplicationCategories } from "~/api/student/application-categories/get"
import { fetchGetStudentCampaigns } from "~/api/student/campaigns/get"
import { fetchGetStudentProfile, GetStudentProfileData } from "~/api/student/profile/get"
import { AchievementsSkill, BasicInfo, Experience, Info, InternshipExperience, JobHunting } from "~/entities/Profile"

/** 卒業年度が入力されていない or 卒業年度が2022年 の時 true */
const isCampaignTarget = (json: GetStudentProfileData): boolean => {
  const { graduates_in } = json.basic_info.name_graduates_in
  return graduates_in == null || graduates_in === 2023
}

export default class ProfileStore {
  @observable json?: GetStudentProfileData

  /** @deprecated {@link ProfileStore.json}を使用する */
  @observable basicInfo: BasicInfo | null = null
  /** @deprecated {@link ProfileStore.json}を使用する */
  @observable experience: Experience | null = null
  /** @deprecated {@link ProfileStore.json}を使用する */
  @observable achievementsSkill: AchievementsSkill | null = null
  /** @deprecated {@link ProfileStore.json}を使用する */
  @observable jobHunting: JobHunting | null = null
  /** @deprecated {@link ProfileStore.json}を使用する */
  @observable internshipExperience: InternshipExperience | null = null
  /** @deprecated {@link ProfileStore.json}を使用する */
  @observable info: Info | null = null

  @observable applicationCategories: HasIdNameDisplay[] = []
  @observable longInternshipPurpose: HasIdDisplay[] = []

  @observable campaignEmptyMap: Record<string, boolean | undefined> = {}
  @observable isAppliedToCampaign?: boolean

  @computed get isValidCampaign(): boolean {
    return false
    //   const { json, isAppliedToCampaign } = this
    //   if (!json || isAppliedToCampaign == null) {
    //     return false
    //   }

    //   // [(卒業年度が入力されていない or 卒業年度が2023年) and キャンペーンに申し込んでないか ] の時にキャンペーンを有効にする
    //   return isCampaignTarget(json) && !isAppliedToCampaign
  }

  @computed get countByEmptyCampaignTarget(): number {
    const entries: Array<[string, boolean | undefined]> = Object.entries(this.campaignEmptyMap)
    return entries.filter(([, isTarget]) => isTarget).length
  }

  /**
   * @example
   * 自己PRを指定するとき、isCampaignTargetの部分は未入力とカウントしたい時はtrueを入れる
   * ```ts
   * profileStore.useAdaptToCampaignTarget("basic_info.self_pr", isCampaignTarget)
   * ```
   */
  useAdaptToCampaignTarget(key: string, isTarget: boolean): void {
    useEffect(() => {
      runInAction(() => {
        this.campaignEmptyMap = { ...this.campaignEmptyMap, [key]: isTarget }
      })
    }, [key, isTarget])
  }

  setCampaignTarget(key: string, isTarget: boolean): void {
    runInAction(() => {
      this.campaignEmptyMap = { ...this.campaignEmptyMap, [key]: isTarget }
    })
  }

  fetchProfileDetail = async () => {
    // NOTE: インターンの参加目的の情報を非同期で取得する
    this.fetchLongInternshipPurpose()
    // NOTE: fetchApplicationCategoriesを同時に取らないと、キャンペーンの数の表示がおかしくなる
    const promises = Promise.all([fetchGetStudentProfile(), this.fetchApplicationCategories()])
    const [result] = await promises
    if (!result.error && result.data) {
      const _result = result as any
      runInAction(() => {
        this.json = result.data
        this.basicInfo = new BasicInfo({ ..._result.data.basic_info })
        this.experience = new Experience({ ..._result.data.experiences })
        this.achievementsSkill = new AchievementsSkill({ ..._result.data.achievements_skills })
        this.jobHunting = new JobHunting({ ..._result.data.job_hunting })
        this.internshipExperience = new InternshipExperience({ ..._result.data.internship_experiences })
        this.info = new Info({ ..._result.data.info })
      })
      if (isCampaignTarget(result.data)) {
        const result = await fetchGetStudentCampaigns({ code: "profile_campaign_2021" })
        if (result.result === "FAILED" || !result.data) return
        runInAction(() => (this.isAppliedToCampaign = result.data.applied))
      }
    }
    return {
      json: this.json,
      basicInfo: this.basicInfo,
      experience: this.experience,
      achievementsSkill: this.achievementsSkill,
      jobHunting: this.jobHunting,
      internshipExperience: this.internshipExperience,
      info: this.info,
    }
  }
  fetchApplicationCategories = async (): Promise<HasIdNameDisplay[]> => {
    const result = await fetchGetStudentApplicationCategories()
    if (!result.error && result.data && Array.isArray(result.data.application_categories)) {
      runInAction(() => {
        this.applicationCategories = result.data.application_categories
      })
    }
    return this.applicationCategories
  }
  fetchLongInternshipPurpose = async (): Promise<GetLongInternshipPurpose> => {
    // 取得済みならapiを叩かずそのままの値を返す
    if (this.longInternshipPurpose.length !== 0) {
      return this.longInternshipPurpose
    }

    const result = await fetchGetLongInternshipPurpose()
    if (Array.isArray(result)) {
      runInAction(() => {
        this.longInternshipPurpose = result
      })
      return result
    }
    return []
  }
  fetchSkills = async (): Promise<string[]> => {
    const result = await fetchGetSkills()
    if (!result.error && Array.isArray(result.skills)) {
      return result.skills
    }
    return []
  }
  fetchSchools = async (): Promise<GetMasterSchools["data"]> => {
    const result = await fetchGetMasterSchools()
    if (!result.error && Array.isArray(result.data)) {
      return result.data
    }
    return []
  }
}
